import type { StatusIndicatorProps } from '@cloudscape-design/components';
import type { EvaluationSummary, EvaluationResult, Severity } from '@amzn/helianthus-arbiter-client';
import { EvaluationStatus } from '@amzn/helianthus-arbiter-client';

export type EvaluationOutcome = 'PASS' | 'FAIL' | 'ERROR' | 'INDETERMINATE';

export const OUTCOME_LABEL: Record<string, string> = {
  PASS: 'Pass',
  FAIL: 'Fail',
  ERROR: 'Error',
  INDETERMINATE: 'Indeterminate',
};

const EVALUATION_STATUS_CONFIG: Record<EvaluationStatus, { label: string; type: StatusIndicatorProps.Type }> = {
  [EvaluationStatus.INITIALIZING]: { label: 'Initializing', type: 'pending' },
  [EvaluationStatus.IN_PROGRESS]: { label: 'In progress', type: 'in-progress' },
  [EvaluationStatus.COMPLETED]: { label: 'Completed', type: 'info' },
  [EvaluationStatus.ERROR]: { label: 'Error', type: 'error' },
  [EvaluationStatus.CANCELLED]: { label: 'Cancelled', type: 'stopped' },
};

export const EVALUATION_STATUS_DISPLAY: Record<EvaluationStatus, string> =
  Object.fromEntries(
    Object.entries(EVALUATION_STATUS_CONFIG).map(([k, v]) => [k, v.label]),
  ) as Record<EvaluationStatus, string>;

export const EVALUATION_STATUS_OPTIONS = [
  { label: 'All Statuses', value: '' },
  ...Object.entries(EVALUATION_STATUS_CONFIG).map(([value, { label }]) => ({ label, value })),
];

export function getStatusType(status: EvaluationStatus): StatusIndicatorProps.Type {
  return EVALUATION_STATUS_CONFIG[status]?.type ?? 'info';
}

export function getOutcomeType(outcome: EvaluationOutcome): StatusIndicatorProps.Type {
  switch (outcome) {
    case 'PASS':
      return 'success';
    case 'FAIL':
      return 'error';
    case 'ERROR':
      return 'error';
    case 'INDETERMINATE':
      return 'warning';
    default:
      return 'info';
  }
}

export const UNKNOWN_SUBJECT = 'Unknown subject';

export function formatSubject(evaluation: EvaluationSummary): string {
  if (evaluation.subject?.awsResource) {
    const { resourceType, resourceId } = evaluation.subject.awsResource;
    return `${resourceType}: ${resourceId}`;
  }
  if (evaluation.subject?.codePackage) {
    const { packageName, commitId } = evaluation.subject.codePackage;
    const commit = shortCommit(commitId);
    return commit ? `${packageName}@${commit}` : (packageName || UNKNOWN_SUBJECT);
  }
  if (evaluation.subject?.codeDiff) {
    const { packageName } = evaluation.subject.codeDiff;
    return packageName || 'Code Diff';
  }
  if (evaluation.subject?.codeReview) {
    const { crId, revisionId } = evaluation.subject.codeReview;
    if (!crId) return UNKNOWN_SUBJECT;
    return revisionId != null ? `${crId} r${revisionId}` : crId;
  }
  return UNKNOWN_SUBJECT;
}

export type ConfidenceLevel = 'high' | 'medium' | 'low' | 'none';

export function getConfidenceLevel(confidence: number | undefined | null): ConfidenceLevel {
  if (confidence == null) return 'none';
  if (confidence >= 0.8) return 'high';
  if (confidence >= 0.5) return 'medium';
  return 'low';
}

export function getConfidenceIndicatorType(level: ConfidenceLevel): StatusIndicatorProps.Type {
  switch (level) {
    case 'high': return 'success';
    case 'medium': return 'warning';
    case 'low': return 'error';
    case 'none': return 'stopped';
  }
}

/**
 * Parse a ruleId string (format "ruleName@version") into its components.
 *
 * Edge cases:
 * - No "@" suffix: returns { ruleName: ruleId, version: undefined }
 * - Non-numeric suffix after "@": treats entire string as the name
 * - Empty/null/undefined: returns { ruleName: '', version: undefined }
 */
export function parseRuleId(ruleId: string | null | undefined): { ruleName: string; version: number | undefined } {
  if (!ruleId) return { ruleName: '', version: undefined };

  const atIndex = ruleId.lastIndexOf('@');
  if (atIndex === -1) return { ruleName: ruleId, version: undefined };

  const name = ruleId.substring(0, atIndex);
  const suffix = ruleId.substring(atIndex + 1);
  const parsed = Number(suffix);

  if (!name || !suffix || !Number.isInteger(parsed) || parsed < 0) {
    return { ruleName: ruleId, version: undefined };
  }

  return { ruleName: name, version: parsed };
}

export function buildRuleUrl(ruleName: string, version?: number | string | null): string {
  const base = `/rules/${encodeURIComponent(ruleName)}`;
  return version != null && version !== '' ? `${base}?version=${version}` : base;
}

/** The inverse of parseRuleId, matching how the service names a result's rule: `name@version`. */
export function buildRuleId(ruleName: string, version?: number | string | null): string {
  return version != null && version !== '' ? `${ruleName}@${version}` : ruleName;
}

export type SubjectKind = 'codeDiff' | 'codePackage' | 'codeReview' | 'awsResource';

export interface SubjectField {
  label: string;
  value: string;
  /** External URL (e.g. code.amazon.com) to render the value as a link. */
  href?: string;
  /** Short tag rendered as a badge beside the value. */
  badge?: string;
}

export interface SubjectPresentation {
  kind: SubjectKind | null;
  typeLabel: string;
  fields: SubjectField[];
}

const SHORT_COMMIT_LENGTH = 8;

function shortCommit(commitId: string | undefined | null): string | undefined {
  return commitId ? commitId.slice(0, SHORT_COMMIT_LENGTH) : undefined;
}

function presentFields(
  candidates: Array<[string, string | undefined | null, string?, string?]>,
): SubjectField[] {
  return candidates
    .filter((entry): entry is [string, string, string?, string?] => entry[1] != null && entry[1] !== '')
    .map(([label, value, href, badge]) => ({
      label,
      value,
      ...(href ? { href } : {}),
      ...(badge ? { badge } : {}),
    }));
}

export function buildPackageUrl(packageName: string): string {
  return `https://code.amazon.com/packages/${encodeURIComponent(packageName)}`;
}

export function buildCommitUrl(packageName: string, commitId: string): string {
  return `${buildPackageUrl(packageName)}/commits/${encodeURIComponent(commitId)}`;
}

export function buildBindleUrl(bindleLock: string): string {
  return `https://bindles.amazon.com/resource/${encodeURIComponent(bindleLock)}`;
}

function isTerminalStatus(status: EvaluationStatus | undefined): boolean {
  return (
    status === EvaluationStatus.COMPLETED ||
    status === EvaluationStatus.ERROR ||
    status === EvaluationStatus.CANCELLED
  );
}

/** A tip-of-branch scan has no commitId until initialization records the one it cloned. */
function packageCommitLabel(
  commitId: string | undefined,
  branchName: string | undefined,
  status: EvaluationStatus | undefined,
): string {
  const commit = shortCommit(commitId);
  if (commit) return commit;
  // The type doesn't promise branchName; don't guess mainline.
  return isTerminalStatus(status) ? 'Not determined' : `Resolving tip of ${branchName || 'the branch'}…`;
}

export function getSubjectPresentation(evaluation: EvaluationSummary): SubjectPresentation {
  const subject = evaluation.subject;

  if (subject?.codeDiff) {
    const { packageName, branch, versionSet, baseCommitId } = subject.codeDiff;
    return {
      kind: 'codeDiff',
      typeLabel: 'Code Diff',
      fields: presentFields([
        ['Package', packageName, packageName ? buildPackageUrl(packageName) : undefined],
        ['Branch', branch],
        ['Version Set', versionSet],
        [
          'Base Commit',
          shortCommit(baseCommitId),
          packageName && baseCommitId ? buildCommitUrl(packageName, baseCommitId) : undefined,
        ],
      ]),
    };
  }

  if (subject?.codePackage) {
    const { packageName, commitId, commitIdResolved, branchName, versionSet } = subject.codePackage;
    return {
      kind: 'codePackage',
      typeLabel: 'Package Scan',
      fields: presentFields([
        ['Package', packageName, packageName ? buildPackageUrl(packageName) : undefined],
        [
          'Commit',
          packageCommitLabel(commitId, branchName, evaluation.status),
          packageName && commitId ? buildCommitUrl(packageName, commitId) : undefined,
          commitId && commitIdResolved ? 'Auto-detected' : undefined,
        ],
        ['Branch', branchName],
        ['Version Set', versionSet],
      ]),
    };
  }

  if (subject?.codeReview) {
    const { crId, revisionId } = subject.codeReview;
    return {
      kind: 'codeReview',
      typeLabel: 'Code Review',
      fields: presentFields([
        ['Code Review', crId],
        ['Revision', revisionId != null ? String(revisionId) : undefined],
      ]),
    };
  }

  if (subject?.awsResource) {
    const { resourceType, resourceId } = subject.awsResource;
    return {
      kind: 'awsResource',
      typeLabel: 'AWS Resource',
      fields: presentFields([
        ['Resource Type', resourceType],
        ['Resource ID', resourceId],
      ]),
    };
  }

  return { kind: null, typeLabel: '', fields: [] };
}

export interface RuleGroup {
  ruleId: string;
  ruleName: string;
  version: number | undefined;
  outcome: EvaluationOutcome;
  severity: Severity | undefined;
  results: EvaluationResult[];
}

export function rollUpOutcome(results: EvaluationResult[]): EvaluationOutcome {
  const outcomes = new Set(results.map((r) => r.outcome as EvaluationOutcome));
  if (outcomes.has('FAIL')) return 'FAIL';
  if (outcomes.has('ERROR')) return 'ERROR';
  if (outcomes.has('INDETERMINATE')) return 'INDETERMINATE';
  return 'PASS';
}

export const OUTCOME_RANK: Record<EvaluationOutcome, number> = {
  FAIL: 0,
  ERROR: 1,
  INDETERMINATE: 2,
  PASS: 3,
};

export function groupResultsByRule(results: EvaluationResult[]): RuleGroup[] {
  const byRuleId = new Map<string, EvaluationResult[]>();
  for (const result of results) {
    const key = result.ruleId ?? '';
    const existing = byRuleId.get(key);
    if (existing) {
      existing.push(result);
    } else {
      byRuleId.set(key, [result]);
    }
  }

  const groups: RuleGroup[] = [];
  for (const [ruleId, groupResults] of byRuleId) {
    const { ruleName, version } = parseRuleId(ruleId);
    groups.push({
      ruleId,
      ruleName,
      version,
      outcome: rollUpOutcome(groupResults),
      severity: rollUpSeverity(groupResults),
      results: groupResults,
    });
  }

  return groups.sort((a, b) => {
    const rank = OUTCOME_RANK[a.outcome] - OUTCOME_RANK[b.outcome];
    return rank !== 0 ? rank : a.ruleName.localeCompare(b.ruleName);
  });
}

const SEVERITY_RANK: Record<Severity, number> = {
  CRITICAL: 0,
  HIGH: 1,
  MEDIUM: 2,
  LOW: 3,
};

const UNRANKED_SEVERITY = Number.MAX_SAFE_INTEGER;

export function rollUpSeverity(results: EvaluationResult[]): Severity | undefined {
  let worst: Severity | undefined;
  for (const result of results) {
    const severity = result.severity;
    if (severity == null) continue;
    if (worst == null || SEVERITY_RANK[severity] < SEVERITY_RANK[worst]) {
      worst = severity;
    }
  }
  return worst;
}

function severityRank(group: RuleGroup): number {
  return group.severity == null ? UNRANKED_SEVERITY : SEVERITY_RANK[group.severity];
}

export function compareRuleGroupBySeverity(a: RuleGroup, b: RuleGroup): number {
  return severityRank(a) - severityRank(b);
}

export function compareRuleGroupByOutcome(a: RuleGroup, b: RuleGroup): number {
  return OUTCOME_RANK[a.outcome] - OUTCOME_RANK[b.outcome];
}

export function compareRuleGroupByResultCount(a: RuleGroup, b: RuleGroup): number {
  return a.results.length - b.results.length;
}
